import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Chip,
  Alert,
  Divider,
  CircularProgress,
  Stack,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import {
  disableMfa,
  setMfaFrequency,
} from "../../../../utils/services/control/mfaService";

/**
 * Dialog administrativo para gestionar el 2FA de otro usuario.
 *
 * Funciones:
 *  - Ver el estado actual del 2FA del usuario (Active / Inactive)
 *  - Cambiar la frecuencia en días (1-365) con la que el usuario debe
 *    re-verificar durante el login
 *  - Desactivar el 2FA del usuario (típicamente cuando perdió el celular y
 *    todos sus códigos de recuperación)
 *
 * Props:
 *   open: boolean
 *   user: { employeeId, firstName, lastName, mail, mfaEnabled, mfaFrequencyDays }
 *   onClose: () => void
 *   onSuccess: (message: string) => void   — ejecutado tras operación exitosa
 *   onError:   (message: string) => void
 */
const MfaManageDialog = ({ open, user, onClose, onSuccess, onError }) => {
  const [frequencyDays, setFrequencyDays] = useState(14);
  const [savingFreq, setSavingFreq] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localMfaEnabled, setLocalMfaEnabled] = useState(false);

  // Sincronizar estado local cuando cambia el usuario
  useEffect(() => {
    if (user) {
      setFrequencyDays(user.mfaFrequencyDays ?? 14);
      setLocalMfaEnabled(!!user.mfaEnabled);
      setLocalError(null);
    }
  }, [user]);

  const handleSaveFrequency = useCallback(async () => {
    if (!user?.employeeId) return;
    const n = Number(frequencyDays);
    if (!Number.isInteger(n) || n < 1 || n > 365) {
      setLocalError("Frequency must be an integer between 1 and 365.");
      return;
    }
    setLocalError(null);
    setSavingFreq(true);
    try {
      const res = await setMfaFrequency(user.employeeId, n);
      if (!res?.success) {
        setLocalError(res?.message || "Could not update frequency.");
        onError?.(res?.message || "Could not update frequency.");
        return;
      }
      onSuccess?.(`MFA frequency updated to ${n} day(s) for ${user.firstName} ${user.lastName}.`);
    } catch (err) {
      setLocalError("Network error updating frequency.");
      onError?.("Network error updating frequency.");
    } finally {
      setSavingFreq(false);
    }
  }, [user, frequencyDays, onSuccess, onError]);

  const handleDisable = useCallback(async () => {
    if (!user?.employeeId) return;
    setLocalError(null);
    setDisabling(true);
    try {
      const res = await disableMfa(user.employeeId);
      if (!res?.success) {
        setLocalError(res?.message || "Could not disable 2FA.");
        onError?.(res?.message || "Could not disable 2FA.");
        return;
      }
      setLocalMfaEnabled(false);
      onSuccess?.(`2FA disabled for ${user.firstName} ${user.lastName}.`);
    } catch (err) {
      setLocalError("Network error disabling 2FA.");
      onError?.("Network error disabling 2FA.");
    } finally {
      setDisabling(false);
    }
  }, [user, onSuccess, onError]);

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: "#0C343D" }}>
        Manage 2FA — {user.firstName} {user.lastName}
      </DialogTitle>

      <DialogContent dividers>
        {/* Status */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          {localMfaEnabled ? (
            <ShieldIcon color="success" />
          ) : (
            <ShieldOutlinedIcon color="disabled" />
          )}
          <Typography variant="subtitle1" fontWeight={600}>
            Status:
          </Typography>
          <Chip
            size="small"
            label={localMfaEnabled ? "Active" : "Inactive"}
            color={localMfaEnabled ? "success" : "default"}
          />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Email: {user.mail || "—"}
        </Typography>

        {!localMfaEnabled && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This user has not enrolled 2FA yet. They must activate it from their
            own profile page. You can still configure the verification frequency
            in advance.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Frequency */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Verification frequency
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          How often the user must re-verify their 2FA during login (1–365 days).
          Recommended: Admin/CEO → 1, Care Management → 7, PCP → 14 (default),
          Senior doctors → 30.
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 2 }}>
          <TextField
            label="Days"
            type="number"
            size="small"
            value={frequencyDays}
            onChange={(e) => setFrequencyDays(e.target.value)}
            inputProps={{ min: 1, max: 365 }}
            sx={{ width: 140 }}
          />
          <Button
            variant="contained"
            onClick={handleSaveFrequency}
            disabled={savingFreq || disabling}
            sx={{
              bgcolor: "#0C343D",
              "&:hover": { bgcolor: "#0a2a33" },
              height: 40,
            }}
          >
            {savingFreq ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save"}
          </Button>
        </Box>

        {localMfaEnabled && (
          <>
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Disable 2FA
            </Typography>
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Use only if the user has lost access to their authenticator app
              AND all their recovery codes. They'll need to re-enroll 2FA from
              their profile after you disable it.
            </Alert>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDisable}
              disabled={disabling || savingFreq}
              startIcon={<ShieldOutlinedIcon />}
            >
              {disabling ? <CircularProgress size={20} /> : "Disable 2FA for this user"}
            </Button>
          </>
        )}

        {localError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {localError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={savingFreq || disabling}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MfaManageDialog;
